import React from 'react';
import { Campaign, Technicentre, Prestation, FilterParams } from '../../types';

interface FilterPanelProps {
  show: boolean;
  onToggle: () => void;
  campaigns: Campaign[];
  technicentres: Technicentre[];
  prestations: Prestation[];
  filters: FilterParams;
  onFilterChange: (filters: FilterParams) => void;
  onClearFilters: () => void;
  onNewIntervention?: () => void;
  onSendEmail?: () => void;
  isGOF?: boolean;
}

// ===== Composant FilterPanel =====
const FilterPanel: React.FC<FilterPanelProps> = ({
  show,
  onToggle,
  campaigns,
  technicentres,
  prestations,
  filters,
  onFilterChange,
  onClearFilters,
  isGOF,
}) => {
  const handleFilterChange = (key: keyof FilterParams, value: string) => {
    const nextFilters = { ...filters, [key]: value };
    if (!value) {
      delete nextFilters[key];
    }
    onFilterChange(nextFilters);
  };

  if (!show) return null;

  return (
    <div className="modal-backdrop" onClick={onToggle}>
      <div className="filter-modal" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-800 flex items-center">
            <i className="fas fa-filter mr-3 text-blue-600"></i>
            <span>Filtres de recherche</span>
          </h3>
          <button onClick={onToggle} className="modal-close text-gray-400 hover:text-gray-600 text-2xl">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="space-y-4" style={{maxHeight: 'calc(90vh - 8rem)', overflowY: 'auto', paddingRight: '0.5rem'}}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-flag mr-2"></i>Campagne
              </label>
              <select
                value={filters.campaign || ''}
                onChange={(e) => handleFilterChange('campaign', e.target.value)}
                className="form-input"
              >
                <option value="">Toutes les campagnes</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.nom}>
                    {campaign.nom}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-building mr-2"></i>Technicentre
              </label>
              <select
                value={filters.technicentre || ''}
                onChange={(e) => handleFilterChange('technicentre', e.target.value)}
                className="form-input"
              >
                <option value="">Tous les technicentres</option>
                {technicentres.map((technicentre) => (
                  <option key={technicentre.id} value={technicentre.nom}>
                    {technicentre.nom}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-cogs mr-2"></i>Prestation
              </label>
              <select
                value={filters.prestation || ''}
                onChange={(e) => handleFilterChange('prestation', e.target.value)}
                className="form-input"
              >
                <option value="">Toutes les prestations</option>
                {prestations.map((prestation) => (
                  <option key={prestation.id} value={prestation.nom}>
                    {prestation.nom}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-train mr-2"></i>N° engin
              </label>
              <input
                type="text"
                value={filters.engin || ''}
                onChange={(e) => handleFilterChange('engin', e.target.value)}
                placeholder="Rechercher un engin..."
                className="form-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-search mr-2"></i>Recherche globale
              </label>
              <input
                type="text"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Rechercher..."
                className="form-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-user-clock mr-2"></i>Demandes sans SR
              </label>
              <label className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={filters.sans_sr === '1'}
                  onChange={(e) => handleFilterChange('sans_sr', e.target.checked ? '1' : '')}
                />
                <span className="text-sm text-gray-700">Afficher uniquement les demandes sans date SR</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-calendar-week mr-2"></i>Numéros de semaine
              </label>
              <div className="grid grid-cols-7 gap-2">
                {Array.from({ length: 53 }, (_, i) => i + 1).map(week => (
                  <button
                    key={week}
                    type="button"
                    onClick={() => {
                      const currentWeeks = filters.numero_semaine ? filters.numero_semaine.split(',') : [];
                      if (currentWeeks.includes(week.toString())) {
                        handleFilterChange('numero_semaine', currentWeeks.filter(w => w !== week.toString()).join(','));
                      } else {
                        handleFilterChange('numero_semaine', [...currentWeeks, week.toString()].join(','));
                      }
                    }}
                    className={`px-2 py-1 text-xs font-semibold rounded transition-all ${
                      filters.numero_semaine?.split(',').includes(week.toString())
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    S{week}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-4 pt-4 border-t">
            <button
              onClick={onClearFilters}
              className="flex-1 btn btn-outline"
            >
              <i className="fas fa-times mr-2"></i> Réinitialiser
            </button>
            <button
              onClick={onToggle}
              className="flex-1 btn btn-primary"
            >
              <i className="fas fa-check mr-2"></i> Appliquer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
